import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  ValidatedResponse,
  QueryParams,
  PagedResponse,
  PatchWithMeta,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";
import {
  fromApiModel,
  toApiModel,
  SvrSubmission,
} from "../domain/SvrSubmission";

export class SvrService extends BaseRemoteService {
  public async get(id: string, opts: Opts): Promise<SvrSubmission> {
    const url = `${this.apiRoot}/submissions/${id}`;
    // paged response is returned be fix the pageSize
    const params = {pageSize: 9999}
    const tagsUrl = `${this.apiRoot}/resourcetags/${id}${paramsToSearchString(params)}`;

    const [svrData, resourceTagData] = await Promise.all([
      super.getRequest<Model>(url, opts),  
      super.getRequest<PagedResponse<Model>>(tagsUrl, opts)
    ]);
    svrData.resourceTags = resourceTagData.data && resourceTagData.data.map((item: Model) => item.tagId);
  
    return fromApiModel(svrData);
  }

  public async createSubmission(
    submission: SvrSubmission,
    opts: Opts
  ): Promise<ValidatedResponse<string>> {
    const url = `${this.apiRoot}/submissions`;
    try {
      const apiModel = toApiModel(submission);
      delete apiModel.resourceTags;
      const id = await super.postRequest<string>(
        url,
        apiModel,
        opts
      );

      // if tags added then post to resourcetags endpoint
      if (submission.resourceTags.length) {
        const params = {pageSize: 9999}
        const tagsUrl = `${this.apiRoot}/resourcetags/${id}${paramsToSearchString(params)}`;
        const model = {resourceType: "SVR", tagIds: submission.resourceTags}
        await super.postRequest(
          tagsUrl,
          model,
          opts
        );
      }
      
      return {
        isValid: true,
        data: id,
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<SvrSubmission>> {
    const url = `${this.apiRoot}/submissions/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);

    return PagedResponseTo<SvrSubmission>(response, fromApiModel);
  }

  public async updateSubmission(
    id: string,
    patchDocument: PatchWithMeta<SvrSubmission>,
    opts: Opts
  ): Promise<ValidatedResponse<Model>> {
    const url = `${this.apiRoot}/submissions/${id}`;
    const tagUrl = `${this.apiRoot}/resourcetags/${id}`;
    try {
      const resourceTagPatch = patchDocument.document.find(patch => patch.path === "/resourceTags");

      if (resourceTagPatch) {
        patchDocument.document = patchDocument.document.filter(x => x !== resourceTagPatch);
        const model = {resourceType: "SVR", tagIds: resourceTagPatch.value} 
        await super.postRequest(
          tagUrl,
          model,
          opts
        );
      }
      await this.patchRequest<SvrSubmission, PatchWithMeta<SvrSubmission>>(
        url,
        patchDocument,
        opts
      );

      const response = await this.get(id, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async deactivateSubmission(
    id: string,
    opts: Opts
  ): Promise<SvrSubmission> {
    const url = `${this.apiRoot}/submissions/${id}/deactivate`
    const response = await super.postRequest<SvrSubmission>(url, null, opts);

    return response;
  }
}
